import { useState } from "react";
import {
  Card, Table, Button, Modal, Form, Input, Switch, InputNumber,
  Tag, Popconfirm, message, Tooltip,
} from "antd";
import { Plus, Pencil, Trash2, Flag } from "lucide-react";
import {
  useFeatureFlagList,
  useFeatureFlagSave,
  useFeatureFlagDelete,
  useFeatureFlagToggle,
} from "../../../api-hooks/featureFlags";
import { TFeatureFlag } from "../../../types";
import type { ColumnsType } from "antd/es/table";

// rollout_pct: percentage of users who see this flag when is_enabled=true — 100 = everyone, 0 = nobody
const getRolloutColor = (pct: number): string => {
  if (pct === 0) return "default";
  if (pct < 50) return "orange";
  if (pct < 100) return "blue";
  return "green";
};

export default function FeatureFlags() {
  const [showModal, setShowModal] = useState(false);
  const [editingFlag, setEditingFlag] = useState<TFeatureFlag | null>(null);
  const [messageApi, ctx] = message.useMessage();
  const [form] = Form.useForm();

  const { data: flags = [], isLoading } = useFeatureFlagList();

  const { mutate: saveFlag, isPending: saving } = useFeatureFlagSave({
    onSuccess: () => {
      messageApi.success(editingFlag ? "Flag updated." : "Flag created.");
      setShowModal(false);
      form.resetFields();
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to save flag."),
  });

  const { mutate: deleteFlag } = useFeatureFlagDelete({
    onSuccess: () => messageApi.success("Flag deleted."),
    onError: () => messageApi.error("Failed to delete flag."),
  });

  const { mutate: toggleFlag } = useFeatureFlagToggle({
    onSuccess: () => messageApi.success("Flag toggled."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to toggle flag."),
  });

  const openCreate = () => {
    setEditingFlag(null);
    form.resetFields();
    form.setFieldsValue({ is_enabled: false, rollout_pct: 100 });
    setShowModal(true);
  };

  const openEdit = (flag: TFeatureFlag) => {
    setEditingFlag(flag);
    form.setFieldsValue({
      ...flag,
      config: flag.config ? JSON.stringify(flag.config, null, 2) : "",
    });
    setShowModal(true);
  };

  const handleSubmit = (vals: any) => {
    let config: Record<string, any> | null = null;
    if (vals.config && vals.config.trim()) {
      try {
        config = JSON.parse(vals.config);
      } catch {
        messageApi.error("Config must be valid JSON.");
        return;
      }
    }
    saveFlag({ ...vals, config, id: editingFlag?.id });
  };

  const columns: ColumnsType<TFeatureFlag> = [
    {
      title: "Flag Key",
      dataIndex: "flag_key",
      key: "flag_key",
      render: (val: string) => (
        <span className="font-mono text-sm text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{val}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (val: string | null) => (
        <span className="text-sm text-slate-500">{val || <span className="italic text-slate-300">—</span>}</span>
      ),
    },
    {
      title: (
        <Tooltip title="Toggle to enable/disable the flag for the configured rollout percentage of users">
          Enabled
        </Tooltip>
      ),
      dataIndex: "is_enabled",
      key: "is_enabled",
      align: "center" as const,
      render: (val: boolean, record: TFeatureFlag) => (
        // Clicking the switch calls the toggle API endpoint directly — no save needed
        <Switch
          checked={val}
          size="small"
          onChange={() => toggleFlag(record.id)}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Percentage of users who see this flag when it is enabled — 100 = everyone, 0 = nobody">
          Rollout %
        </Tooltip>
      ),
      dataIndex: "rollout_pct",
      key: "rollout_pct",
      align: "center" as const,
      render: (val: number) => (
        <Tag color={getRolloutColor(val)}>{val}%</Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleDateString("en-GB")}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TFeatureFlag) => (
        <div className="flex gap-2 justify-end">
          <Button size="small" icon={<Pencil size={12} />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this feature flag?"
            description="This cannot be undone. Services checking this flag will fall back to defaults."
            onConfirm={() => deleteFlag(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<Trash2 size={12} />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Feature Flags</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Control which platform features are enabled and for what percentage of users
          </p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={openCreate}
          style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
        >
          New Flag
        </Button>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        {/* No pagination — feature flags are a small set (<50), always show all */}
        <Table
          dataSource={flags}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          size="middle"
          locale={{ emptyText: "No feature flags yet. Create your first flag." }}
        />
      </Card>

      <Modal
        open={showModal}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        footer={null}
        title={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <Flag size={15} className="text-violet-600" />
            </div>
            <span>{editingFlag ? `Edit: ${editingFlag.flag_key}` : "Create Feature Flag"}</span>
          </div>
        }
        width={540}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
          <Form.Item
            name="flag_key"
            label="Flag Key"
            extra="Snake_case identifier used in code, e.g. new_scheduler_ui"
            rules={[
              { required: true, message: "Flag key is required" },
              { pattern: /^[a-z0-9_]+$/, message: "Only lowercase letters, numbers and underscores" },
            ]}
          >
            <Input
              className="rounded-lg font-mono"
              placeholder="e.g. new_scheduler_ui"
              maxLength={100}
              disabled={!!editingFlag}
            />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea
              className="rounded-lg"
              rows={2}
              placeholder="Brief description of what this flag controls"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            {/* rollout_pct: 0–100 — what % of users see this feature when is_enabled is true */}
            <Form.Item
              name="rollout_pct"
              label="Rollout %"
              extra="0 = nobody, 100 = all users"
              rules={[{ type: "number", min: 0, max: 100, message: "Must be 0–100" }]}
            >
              <InputNumber min={0} max={100} className="w-full rounded-lg" placeholder="100" />
            </Form.Item>

            <div className="flex gap-6 pt-6">
              <Form.Item name="is_enabled" valuePropName="checked" label="Enabled" className="mb-0">
                <Switch />
              </Form.Item>
            </div>
          </div>

          <Form.Item
            name="config"
            label="Config (JSON)"
            extra="Optional JSON object — extra configuration passed to the flag consumer"
          >
            <Input.TextArea
              className="rounded-lg font-mono text-xs"
              rows={4}
              placeholder={'{\n  "variant": "v2",\n  "timeout_ms": 5000\n}'}
            />
          </Form.Item>

          <div className="flex gap-2 justify-end mt-2 pt-4 border-t border-slate-100">
            <Button onClick={() => { setShowModal(false); form.resetFields(); }}>Cancel</Button>
            <Button
              htmlType="submit"
              type="primary"
              loading={saving}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              {editingFlag ? "Update Flag" : "Create Flag"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
